import React, { useEffect, useState } from 'react';
import {
  Sparkles, Loader2, ChevronDown, AlertTriangle, Wrench, FileText, Clock, Wallet, Camera, CheckCircle2,
} from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { toast } from 'sonner';

const RISK_BADGE = {
  Low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200',
  High: 'bg-orange-50 text-orange-700 border-orange-200',
  Critical: 'bg-red-50 text-red-700 border-red-200',
  Unknown: 'bg-slate-100 text-slate-600 border-slate-200',
};

const RiskBadge = ({ level, testid }) => (
  <span data-testid={testid}
    className={`inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-display font-bold uppercase tracking-wider border ${RISK_BADGE[level] || RISK_BADGE.Unknown}`}>
    {level || 'Unknown'}
  </span>
);

const Bullets = ({ title, items, icon: Icon, accent }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className={`text-[11px] font-display font-bold uppercase tracking-widest mb-1.5 flex items-center gap-1.5 ${accent}`}>
        <Icon size={12} /> {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-[13px] text-slate-700 leading-snug flex gap-2">
            <span className="text-slate-300 shrink-0">▪</span><span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Collapsed "Context used" — shows the data Claude actually saw (trust, not black box).
const ContextUsed = ({ context, testid }) => {
  if (!context) return null;
  const sources = context.data_sources_present || [];
  return (
    <details className="mt-4 group" data-testid={testid}>
      <summary className="cursor-pointer list-none flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-widest text-slate-500 hover:text-[#3b4872] select-none">
        <ChevronDown size={13} className="transition-transform group-open:rotate-180" />
        Context used
        {sources.length > 0 && (
          <span className="normal-case font-normal tracking-normal text-slate-400">
            · {sources.join(', ')}
          </span>
        )}
      </summary>
      <pre className="mt-2 text-[11px] leading-relaxed bg-slate-900 text-slate-200 rounded-sm p-3 overflow-x-auto max-h-72 overflow-y-auto font-mono">
        {JSON.stringify(context, null, 2)}
      </pre>
    </details>
  );
};

const Card = ({ title, overline, icon: Icon, children, action, testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm" data-testid={testid}>
    <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-sm bg-[#3b4872]/10 text-[#3b4872] flex items-center justify-center"><Icon size={15} /></span>
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-400">{overline}</div>
          <h3 className="font-display font-bold text-base leading-tight">{title}</h3>
        </div>
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const GenBtn = ({ onClick, loading, label, testid }) => (
  <button onClick={onClick} disabled={loading} data-testid={testid}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5c] disabled:opacity-50 transition-colors">
    {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
    {loading ? 'Analysing…' : label}
  </button>
);

function DelayCard({ projectId }) {
  const [pred, setPred] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const d = await apiGet(`/ai/projects/${projectId}/delay-prediction/history`);
      setHistory(d.history || []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [projectId]);

  const run = async () => {
    setLoading(true);
    try {
      const d = await apiGet(`/ai/projects/${projectId}/delay-prediction`);
      setPred({ ...d.prediction, context: d.context });
      loadHistory();
      toast.success('Delay prediction generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate delay prediction');
    } finally { setLoading(false); }
  };

  return (
    <Card title="Schedule Delay Prediction" overline="AI · Ellora AI" icon={Clock} testid="ai-delay-card"
      action={<GenBtn onClick={run} loading={loading} label={pred ? 'Re-run' : 'Generate'} testid="ai-delay-generate" />}>
      {!pred ? (
        <p className="text-sm text-slate-400">
          Generate an AI assessment of schedule-delay risk from live EVM, baseline variance, DPR discipline,
          hindrances, procurement and quality signals.
        </p>
      ) : (
        <div className="space-y-4" data-testid="ai-delay-result">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-400">Risk</span>
              <RiskBadge level={pred.risk_level} testid="ai-delay-risk" />
            </div>
            <div>
              <span className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-400 mr-2">Predicted delay</span>
              <span className="font-display font-extrabold text-lg">{pred.predicted_delay_days || '—'}</span>
              <span className="text-xs text-slate-500 ml-1">days</span>
            </div>
            <div>
              <span className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-400 mr-2">Confidence</span>
              <span className="font-display font-semibold text-sm">{pred.confidence || '—'}</span>
            </div>
          </div>
          <Bullets title="Top risks" items={pred.top_risks} icon={AlertTriangle} accent="text-red-600" />
          <Bullets title="Recommended actions" items={pred.recommended_actions} icon={Wrench} accent="text-[#3b4872]" />
          <ContextUsed context={pred.context} testid="ai-delay-context" />
        </div>
      )}
      {history.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 mb-2">Recent assessments</div>
          <div className="space-y-1">
            {history.slice(0, 5).map((h) => (
              <div key={h.id} className="flex items-center justify-between text-xs text-slate-500">
                <span>{formatDate(h.generated_at)}</span>
                <span className="flex items-center gap-2">
                  <span>{h.predicted_delay_days || '—'}d</span>
                  <RiskBadge level={h.risk_level} />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function CostCard({ projectId }) {
  const [pred, setPred] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const d = await apiGet(`/ai/projects/${projectId}/cost-prediction/history`);
      setHistory(d.history || []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [projectId]);

  const run = async () => {
    setLoading(true);
    try {
      const d = await apiGet(`/ai/projects/${projectId}/cost-prediction`);
      setPred({ ...d.prediction, context: d.context });
      loadHistory();
      toast.success('Cost prediction generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate cost prediction');
    } finally { setLoading(false); }
  };

  return (
    <Card title="Cost Overrun Prediction" overline="AI · Ellora AI" icon={Wallet} testid="ai-cost-card"
      action={<GenBtn onClick={run} loading={loading} label={pred ? 'Re-run' : 'Generate'} testid="ai-cost-generate" />}>
      {!pred ? (
        <p className="text-sm text-slate-400">
          Generate an AI assessment of budget-overrun risk from full EVM (CPI/SPI/EAC), procurement commitment,
          labour man-days and client-attributable delays.
        </p>
      ) : (
        <div className="space-y-4" data-testid="ai-cost-result">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-400">Overrun risk</span>
              <RiskBadge level={pred.overrun_risk || pred.risk_level} testid="ai-cost-risk" />
            </div>
            <div>
              <span className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-400 mr-2">Confidence</span>
              <span className="font-display font-semibold text-sm">{pred.confidence || '—'}</span>
            </div>
          </div>
          {pred.predicted_final_cost && (
            <div className="text-sm"><span className="text-slate-400 font-display font-bold uppercase tracking-widest text-[11px] mr-2">Predicted final cost</span><span className="font-display font-semibold">{pred.predicted_final_cost}</span></div>
          )}
          {pred.predicted_variance && (
            <div className="text-sm"><span className="text-slate-400 font-display font-bold uppercase tracking-widest text-[11px] mr-2">Predicted variance</span><span className="font-display font-semibold">{pred.predicted_variance}</span></div>
          )}
          <Bullets title="Top cost risks" items={pred.top_cost_risks} icon={AlertTriangle} accent="text-red-600" />
          <Bullets title="Recommended actions" items={pred.recommended_actions} icon={Wrench} accent="text-[#3b4872]" />
          <ContextUsed context={pred.context} testid="ai-cost-context" />
        </div>
      )}
      {history.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 mb-2">Recent assessments</div>
          <div className="space-y-1">
            {history.slice(0, 5).map((h) => (
              <div key={h.id} className="flex items-center justify-between text-xs text-slate-500">
                <span>{formatDate(h.generated_at)}</span>
                <RiskBadge level={h.overrun_risk || h.risk_level} />
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function DPRSummaryCard({ projectId }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [summary, setSummary] = useState(null);
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      const d = await apiPost(`/ai/projects/${projectId}/dpr-summary${qs ? `?${qs}` : ''}`);
      setSummary(d.summary);
      setCount(d.dpr_count);
      if (d.dpr_count === 0) toast.message('No DPRs found in the selected range');
      else toast.success(`Summarised ${d.dpr_count} DPR(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate DPR summary');
    } finally { setLoading(false); }
  };

  const inputCls = 'px-2.5 py-1.5 text-sm border border-slate-300 rounded-sm focus:border-[#3b4872] focus:outline-none';

  return (
    <Card title="DPR Executive Summary" overline="AI · Ellora AI" icon={FileText} testid="ai-dpr-card"
      action={<GenBtn onClick={run} loading={loading} label="Summarise" testid="ai-dpr-generate" />}>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="text-xs text-slate-500">
          <span className="block font-display font-bold uppercase tracking-widest text-[10px] text-slate-400 mb-1">From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} data-testid="ai-dpr-from" />
        </label>
        <label className="text-xs text-slate-500">
          <span className="block font-display font-bold uppercase tracking-widest text-[10px] text-slate-400 mb-1">To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid="ai-dpr-to" />
        </label>
        <span className="text-xs text-slate-400 pb-2">Leave blank to summarise all reports.</span>
      </div>
      {summary != null && (
        count === 0 ? (
          <p className="text-sm text-slate-400" data-testid="ai-dpr-empty">No Daily Progress Reports found in the selected date range.</p>
        ) : (
          <div className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 border border-slate-100 rounded-sm p-3" data-testid="ai-dpr-summary">
            {summary}
          </div>
        )
      )}
    </Card>
  );
}

function PhotoVerificationsCard({ projectId }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [disc, setDisc] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiGet(`/ai/projects/${projectId}/photo-verifications?days=7`).then((d) => {
      if (!alive) return;
      setRows(d.items || []); setCount(d.count || 0); setDisc(d.discrepancies || 0);
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [projectId]);

  return (
    <Card title="DPR Photo Verifications" overline="AI · Vision (last 7 days)" icon={Camera} testid="ai-photo-verify-card">
      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : count === 0 ? (
        <p className="text-sm text-slate-400" data-testid="ai-photo-verify-empty">
          No photo verifications in the last 7 days. Run &quot;Photo Verify&quot; on a DPR to cross-check reported progress against site photos.
        </p>
      ) : (
        <div className="space-y-2" data-testid="ai-photo-verify-list">
          <div className="text-xs text-slate-500">{count} verification(s) · <b className={disc ? 'text-rose-600' : 'text-emerald-600'}>{disc} discrepancy flagged</b></div>
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 text-sm border border-slate-100 rounded-sm px-3 py-2" data-testid={`ai-photo-verify-row-${r.id}`}>
              {r.discrepancy_flag
                ? <AlertTriangle size={15} className="text-rose-600 shrink-0" />
                : <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />}
              <span className="text-slate-500 text-xs w-24 shrink-0">{formatDate(r.dpr_date || r.verified_at)}</span>
              <span className="text-slate-700 truncate flex-1">{r.activity_name || 'Site work'}</span>
              <span className="text-xs whitespace-nowrap">AI ~{r.avg_visible_pct ?? '—'}% · Rep {r.reported_pct ?? '—'}%</span>
              {r.discrepancy_flag && <span className="text-[10px] font-bold uppercase text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded-sm">Flagged</span>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


export default function AIInsightsTab({ project }) {
  return (
    <div className="space-y-5" data-testid="ai-insights-tab">
      <div className="flex items-start gap-2.5">
        <span className="w-9 h-9 rounded-sm bg-gradient-to-br from-[#3b4872] to-[#5b6aa8] text-white flex items-center justify-center shrink-0"><Sparkles size={17} /></span>
        <div>
          <h2 className="font-display font-bold text-lg leading-tight">AI Intelligence</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Ellora AI predictions over live ERP data. Each assessment shows the exact context used — expand
            “Context used” to see what drove it. Generated assessments are stored and feed the CEO Command Center.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <DelayCard projectId={project.id} />
        <CostCard projectId={project.id} />
      </div>
      <DPRSummaryCard projectId={project.id} />
      <PhotoVerificationsCard projectId={project.id} />
    </div>
  );
}
